import React, { useState, useEffect } from "react";
import { X, Save, Loader2, Settings, User } from "lucide-react";
import { useAgents } from "../../hooks/useAgents";

const EditChannelModal = ({
  isOpen,
  onClose,
  onSave,
  channelData,
  isSaving = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    status: "active",
    business_name: "",
    agent_id: "",
  });
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch agents using hook
  const { data: agentsData, isLoading: loadingAgents } = useAgents();
  const agents = agentsData?.agents || [];

  // Initialize form when modal opens or channel data changes
  useEffect(() => {
    if (isOpen && channelData) {
      const initialData = {
        name: channelData.name || "",
        status: channelData.status || "active",
        business_name: channelData.config?.business_name || "",
        agent_id: channelData.agent_id ? String(channelData.agent_id) : "",
      };
      setFormData(initialData);
      setErrors({});
      setHasChanges(false);
    }
  }, [isOpen, channelData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: "",
        business_name: "",
        agent_id: "",
      });
      setErrors({});
      setHasChanges(false);
    }
  }, [isOpen]);

  // Track changes
  useEffect(() => {
    if (channelData) {
      const originalData = {
        name: channelData.name || "",
        business_name: channelData.config?.business_name || "",
        agent_id: channelData.agent_id ? String(channelData.agent_id) : "",
      };

      const hasAnyChanges = Object.keys(formData).some(
        (key) => formData[key] !== originalData[key]
      );
      setHasChanges(hasAnyChanges);
    }
  }, [formData, channelData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Channel name is required";
    }

    if (!formData.business_name.trim()) {
      newErrors.business_name = "Business name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Convert and validate agent_id
      let agentId = null;
      if (formData.agent_id) {
        agentId = parseInt(formData.agent_id, 10);
        // Validate that it's a valid number
        if (isNaN(agentId)) {
          throw new Error("Invalid agent selection");
        }
      }

      await onSave({
        name: formData.name.trim(),
        status: formData.status,
        agentId: agentId,
        config: {
          business_name: formData.business_name.trim(),
        },
      });
    } catch (error) {
      console.error("Error saving channel:", error);
    }
  };

  const handleClose = () => {
    if (hasChanges && !isSaving) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?"
        )
      ) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape" && !isSaving) {
      handleClose();
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={handleClose}
        ></div>

        {/* Modal content */}
        <div
          className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl"
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Edit Channel Settings
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Update channel configuration and settings
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <div className="space-y-6">
            {/* Channel Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channel Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                disabled={isSaving}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.name
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
                placeholder="Enter channel name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Channel Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Channel Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
                disabled={isSaving}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Agent Assignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Agent Assignment
              </label>
              <div className="relative">
                <select
                  value={formData.agent_id}
                  onChange={(e) =>
                    handleInputChange("agent_id", e.target.value)
                  }
                  disabled={isSaving || loadingAgents}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white appearance-none"
                >
                  <option value="">Off (No Agent Assigned)</option>
                  {loadingAgents ? (
                    <option disabled>Loading agents...</option>
                  ) : (
                    agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))
                  )}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <User className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select "Off" to disable agent assignment for this channel
              </p>
            </div>

            {/* Business Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business Name *
              </label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) =>
                  handleInputChange("business_name", e.target.value)
                }
                disabled={isSaving}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  errors.business_name
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
                placeholder="Enter business name"
              />
              {errors.business_name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.business_name}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-6 mt-8 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Press{" "}
              <kbd className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                Ctrl+Enter
              </kbd>{" "}
              to save
              {hasChanges && (
                <span className="ml-2 text-orange-600 dark:text-orange-400">
                  • Unsaved changes
                </span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditChannelModal;
